import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { SimpleSelectOption } from "@repo/ui/Select/simpleSelect";
import { useMemo } from "react";
import { useAllMinimalPlaybooksQuery } from "../api/entities/playbook.js";
import { useAllTeammateAgentsQuery } from "../api/entities/teammateAgent.js";

export interface PlaybookAndVoiceAgentOptions {
  /** Coaching playbooks, with voice-agent playbooks excluded. */
  playbookOptions: readonly SimpleSelectOption<PlaybookId>[];
  /** Voice agents, each valued by the playbook backing the agent. */
  voiceAgentOptions: readonly SimpleSelectOption<PlaybookId>[];
  isLoading: boolean;
}

export interface PlaybookAndVoiceAgentSection {
  id: string;
  /** Omitted when the menu has a single, unlabeled section. */
  label?: string;
  options: readonly SimpleSelectOption<PlaybookId>[];
}

/**
 * Options for pickers that choose which calls to analyze: coaching playbooks
 * plus voice agents. A voice agent's calls are graded against the playbook
 * backing the agent, so voice agent options carry that playbook's id and no
 * caller needs to treat the two kinds differently.
 */
export function usePlaybookAndVoiceAgentOptions(): PlaybookAndVoiceAgentOptions {
  const { data: playbooks, isLoading: arePlaybooksLoading } =
    useAllMinimalPlaybooksQuery({ shouldExcludeTeammateAgentPlaybooks: true });
  const { data: teammateAgents, isLoading: areTeammateAgentsLoading } =
    useAllTeammateAgentsQuery();

  const playbookOptions = useMemo<readonly SimpleSelectOption<PlaybookId>[]>(
    () =>
      playbooks?.map((playbook) => ({
        value: playbook.id,
        label: playbook.name,
      })) ?? [],
    [playbooks],
  );

  const voiceAgentOptions = useMemo<readonly SimpleSelectOption<PlaybookId>[]>(
    () =>
      teammateAgents?.map((agent) => ({
        value: agent.teammate_agent_config.playbook_id,
        label: agent.name,
      })) ?? [],
    [teammateAgents],
  );

  return {
    playbookOptions,
    voiceAgentOptions,
    isLoading: arePlaybooksLoading || areTeammateAgentsLoading,
  };
}

/**
 * Groups the options into "Playbook" / "Voice agent" sections. With no voice
 * agents the single playbook section is unlabeled, so the menu reads as a
 * plain playbook list.
 */
export function buildPlaybookAndVoiceAgentSections(
  playbookOptions: readonly SimpleSelectOption<PlaybookId>[],
  voiceAgentOptions: readonly SimpleSelectOption<PlaybookId>[],
): readonly PlaybookAndVoiceAgentSection[] {
  if (voiceAgentOptions.length === 0) {
    return [{ id: "playbooks", options: playbookOptions }];
  }

  return [
    { id: "playbooks", label: "Playbook", options: playbookOptions },
    { id: "voice-agents", label: "Voice agent", options: voiceAgentOptions },
  ];
}
